import React from "react";
import {
  Background,
  Handle,
  ConnectionMode,
  ReactFlow,
  ReactFlowProvider,
  Position,
  type Edge,
  type Node,
  useEdgesState,
  useNodesState,
  useReactFlow,
} from "@xyflow/react";
import { FiPlus, FiSend } from "react-icons/fi";
import { TbAlertTriangle } from "react-icons/tb";
import { useSelector } from "react-redux";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";

import DelayBeforeActionModal from "../../components/Campaigns/components/DelayBeforeActionModal";
import SendInMailSidebar, {
  type SendInMailFormPayload,
} from "../../components/Campaigns/components/SendInMailSidebar";
import SequenceConnectionRequestSidebar, {
  type ConnectionRequestFormPayload,
} from "../../components/Campaigns/components/SequenceConnectionRequestSidebar";
import { Button, Modal } from "../../components/ui";
import { useAsyncAction } from "../../hooks/useAsyncAction";
import { ownerSelector, templateSelector } from "../../store/store";
import {
  EMAIL_CHANNEL_REQUIRED_MESSAGE,
  DEFAULT_EMAIL_MESSAGE,
  DEFAULT_EMAIL_SUBJECT,
  getSequenceActionMeta,
  getSequencePathLabel,
  getSequenceStepsFromTemplate,
  isSequenceTemplate,
  type SequenceActionType,
  type SequenceStep,
} from "../../utils/sequenceTemplate";
import { fetchEmailIntegration } from "../../utils";
import {
  type CampaignFlowState,
  fetchCampaignDraft,
  draftSearchFromCampaignId,
  resolveFlowScope,
  resolveCampaignIdFromStateOrSearch,
  upsertCampaignDraft,
} from "./campaignDraftFlow";

type FlowActionType = "ifConnection" | "ifOpenProfile" | "sendConnectionRequest" | "sendInMail" | "sendEmail" | "likePost" | "viewProfile";
type FlowPathPart =
  | "notConnected"
  | "connected"
  | "notOpenProfile"
  | "openProfile"
  | "notAccepted"
  | "accepted"
  | "notReplied"
  | "replied"
  | "next";

type DelayUnit = "days" | "hours";

type FlowSlot = {
  delayDays: number;
  delayUnit: DelayUnit;
  ended: boolean;
  next: FlowNode | null;
};

type MessageVariation = {
  id: string;
  label: string;
  message: string;
};

type ConnectionRequestConfig = {
  isAiMessageEnabled: boolean;
  fallbackMessage: string;
  withdrawAfterDays: number;
  messageVariations: MessageVariation[];
};

type SendInMailConfig = {
  isAiMessageEnabled: boolean;
  subject: string;
  fallbackMessage: string;
  messageVariations: MessageVariation[];
};

type IfConnectionNode = {
  id: string;
  type: "ifConnection";
  notConnected: FlowSlot;
  connected: FlowSlot;
};

type IfOpenProfileNode = {
  id: string;
  type: "ifOpenProfile";
  notOpenProfile: FlowSlot;
  openProfile: FlowSlot;
};

type SendConnectionRequestNode = {
  id: string;
  type: "sendConnectionRequest";
  notAccepted: FlowSlot;
  accepted: FlowSlot;
  config: ConnectionRequestConfig;
};

type LikePostNode = {
  id: string;
  type: "likePost";
  next: FlowSlot;
};

type SendInMailNode = {
  id: string;
  type: "sendInMail" | "sendEmail";
  notReplied: FlowSlot;
  replied: FlowSlot;
  config: SendInMailConfig;
};

type ViewProfileNode = {
  id: string;
  type: "viewProfile";
  next: FlowSlot;
};

type FlowNode = IfConnectionNode | IfOpenProfileNode | SendConnectionRequestNode | LikePostNode | SendInMailNode | ViewProfileNode;

const isReplyBranchNode = (node: FlowNode): node is SendInMailNode =>
  node.type === "sendInMail" || node.type === "sendEmail";

const isReplyBranchType = (type: FlowActionType | null | undefined): type is "sendInMail" | "sendEmail" =>
  type === "sendInMail" || type === "sendEmail";

type GraphNodeData = {
  label: string;
  kind: "root" | "action" | "branch" | "delay" | "cta" | "end";
};

type ActionOption = {
  id: string;
  label: string;
  group: "steps";
  actionType?: FlowActionType;
};

const REPEATABLE_ACTIONS: FlowActionType[] = ["sendInMail", "sendEmail", "viewProfile", "likePost"];
const WARN_ON_REPEAT_ACTIONS: FlowActionType[] = ["viewProfile", "likePost"];
const REPEAT_WARNING_TEXT = "Repeating this action may slow down the campaign";

const ACTION_OPTIONS: ActionOption[] = [
  { id: "step-send-invitation", label: getSequenceActionMeta("sendConnectionRequest")?.label || "Send Invitation", group: "steps", actionType: "sendConnectionRequest" },
  { id: "step-visit-profile", label: getSequenceActionMeta("viewProfile")?.label || "Visit Profile", group: "steps", actionType: "viewProfile" },
  { id: "step-send-message", label: getSequenceActionMeta("sendInMail")?.label || "Send Message", group: "steps", actionType: "sendInMail" },
  { id: "step-send-email", label: getSequenceActionMeta("sendEmail")?.label || "Email", group: "steps", actionType: "sendEmail" },
  { id: "step-delay", label: "Delay", group: "steps" },
];

const ACTION_W = 280;
const BRANCH_W = 180;
const DELAY_W = 160;
const CTA_W = 210;
const END_W = 145;
const X_GAP = 220;
const Y_GAP = 110;

const cardStyle = {
  borderRadius: 12,
  border: "1px solid var(--color-light-grey)",
  boxShadow: "0 2px 6px rgba(0,0,0,0.08)",
  background: "white",
};

const textOnlyStyle = {
  border: "none",
  background: "transparent",
  boxShadow: "none",
  color: "var(--color-dark-black)",
};

const ctaStyle = {
  borderRadius: 12,
  border: "1px solid var(--color-brand-blue)",
  background: "var(--color-brand-blue)",
  color: "white",
};

const endStyle = {
  borderRadius: 12,
  border: "1px solid var(--color-light-grey)",
  background: "white",
};

const makeSlot = (delayDays = 0, delayUnit: DelayUnit = "days"): FlowSlot => ({
  delayDays,
  delayUnit,
  ended: false,
  next: null,
});

const DEFAULT_CONNECTION_REQUEST_CONFIG: ConnectionRequestConfig = {
  isAiMessageEnabled: false,
  fallbackMessage: "",
  withdrawAfterDays: 25,
  messageVariations: [{ id: "message-a", label: "Message A", message: "Hi {{firstName}} - would love to connect" }],
};

const DEFAULT_SEND_INMAIL_CONFIG: SendInMailConfig = {
  isAiMessageEnabled: false,
  subject: "",
  fallbackMessage: "",
  messageVariations: [{ id: "message-a", label: "Message A", message: "" }],
};

const DEFAULT_SEND_EMAIL_CONFIG: SendInMailConfig = {
  isAiMessageEnabled: false,
  subject: DEFAULT_EMAIL_SUBJECT,
  fallbackMessage: "",
  messageVariations: [{ id: "message-a", label: "Message A", message: DEFAULT_EMAIL_MESSAGE }],
};

const makeNode = (type: FlowActionType): FlowNode => {
  const id = `${type}-${Math.random().toString(36).slice(2, 9)}`;
  if (type === "ifConnection") return { id, type, notConnected: makeSlot(0), connected: makeSlot(0) };
  if (type === "ifOpenProfile") return { id, type, notOpenProfile: makeSlot(0), openProfile: makeSlot(0) };
  if (type === "sendConnectionRequest")
    return {
      id,
      type,
      notAccepted: makeSlot(0),
      accepted: makeSlot(0),
      config: DEFAULT_CONNECTION_REQUEST_CONFIG,
    };
  if (type === "sendInMail" || type === "sendEmail")
    return {
      id,
      type,
      notReplied: makeSlot(0),
      replied: { ...makeSlot(0), ended: true },
      config: type === "sendEmail" ? DEFAULT_SEND_EMAIL_CONFIG : DEFAULT_SEND_INMAIL_CONFIG,
    };
  if (type === "likePost") return { id, type, next: makeSlot(0) };
  return { id, type, next: makeSlot(0) };
};

const flowNodeFromTemplateStep = (step: SequenceStep): FlowNode => {
  if (step.actionType === "sendConnectionRequest") {
    const node = makeNode("sendConnectionRequest") as SendConnectionRequestNode;
    node.config = {
      ...DEFAULT_CONNECTION_REQUEST_CONFIG,
      messageVariations: [
        {
          id: "message-a",
          label: "Message A",
          message: step.sendWithoutNote ? "" : String(step.message || ""),
        },
      ],
    };
    return node;
  }
  if (step.actionType === "sendInMail" || step.actionType === "sendEmail") {
    const node = makeNode(step.actionType) as SendInMailNode;
    const defaults =
      step.actionType === "sendEmail" ? DEFAULT_SEND_EMAIL_CONFIG : DEFAULT_SEND_INMAIL_CONFIG;
    node.config = {
      ...defaults,
      subject:
        step.actionType === "sendEmail"
          ? String(step.subject || DEFAULT_EMAIL_SUBJECT)
          : "",
      messageVariations: [
        {
          id: "message-a",
          label: "Message A",
          message: String(step.message || defaults.messageVariations[0]?.message || ""),
        },
      ],
    };
    return node;
  }
  return makeNode("viewProfile");
};

const attachTemplateChild = (
  parent: FlowNode,
  child: FlowNode,
  delayDays: number,
  delayUnit: DelayUnit = "days"
) => {
  const slot = makeSlot(Math.max(0, delayDays), delayUnit === "hours" ? "hours" : "days");
  slot.next = child;
  if (parent.type === "sendConnectionRequest") {
    parent.notAccepted = slot;
    return;
  }
  if (isReplyBranchNode(parent)) {
    parent.notReplied = slot;
    return;
  }
  if (parent.type === "viewProfile" || parent.type === "likePost") {
    parent.next = slot;
  }
};

const buildCampaignRootFromTemplateSteps = (steps: SequenceStep[]): FlowNode | null => {
  if (!steps.length) return null;
  const nodes = steps.map(flowNodeFromTemplateStep);
  for (let index = 0; index < nodes.length - 1; index += 1) {
    attachTemplateChild(
      nodes[index],
      nodes[index + 1],
      Math.max(0, steps[index + 1].delayDays || 0),
      steps[index + 1].delayUnit === "hours" ? "hours" : "days"
    );
  }
  return nodes[0];
};

const buildConnectionRequestConfig = (
  payload?: ConnectionRequestFormPayload
): ConnectionRequestConfig => {
  const messageVariations = payload?.messageVariations;
  return {
    isAiMessageEnabled: Boolean(payload?.isAiMessageEnabled),
    fallbackMessage: payload?.fallbackMessage || "",
    withdrawAfterDays: Number(payload?.withdrawAfterDays || 0),
    messageVariations: Array.isArray(messageVariations)
      ? messageVariations
      : DEFAULT_CONNECTION_REQUEST_CONFIG.messageVariations,
  };
};

const makeConnectionRequestNodeWithConfig = (
  payload?: ConnectionRequestFormPayload
): SendConnectionRequestNode => {
  const base = makeNode("sendConnectionRequest") as SendConnectionRequestNode;
  return {
    ...base,
    config: buildConnectionRequestConfig(payload),
  };
};

const mergeConnectionRequestNodeConfig = (
  node: SendConnectionRequestNode,
  payload?: ConnectionRequestFormPayload
): SendConnectionRequestNode => ({
  ...node,
  config: buildConnectionRequestConfig(payload),
});

const buildSendInMailConfig = (
  payload?: SendInMailFormPayload,
  type: "sendInMail" | "sendEmail" = "sendInMail"
): SendInMailConfig => {
  const defaults = type === "sendEmail" ? DEFAULT_SEND_EMAIL_CONFIG : DEFAULT_SEND_INMAIL_CONFIG;
  const messageVariations = payload?.messageVariations;
  return {
    isAiMessageEnabled: Boolean(payload?.isAiMessageEnabled),
    subject: payload?.subject || defaults.subject,
    fallbackMessage: payload?.fallbackMessage || "",
    messageVariations: Array.isArray(messageVariations)
      ? messageVariations
      : defaults.messageVariations,
  };
};

const makeSendInMailNodeWithConfig = (
  payload?: SendInMailFormPayload,
  type: "sendInMail" | "sendEmail" = "sendInMail"
): SendInMailNode => {
  const base = makeNode(type) as SendInMailNode;
  return {
    ...base,
    config: buildSendInMailConfig(payload, type),
  };
};

const mergeSendInMailNodeConfig = (
  node: SendInMailNode,
  payload?: SendInMailFormPayload
): SendInMailNode => ({
  ...node,
  config: buildSendInMailConfig(payload, node.type),
});

const nodeLabel = (type: FlowActionType) => {
  if (type === "ifConnection") return "If Connection";
  if (type === "ifOpenProfile") return "If Open Profile";
  if (
    type === "sendConnectionRequest" ||
    type === "sendInMail" ||
    type === "sendEmail" ||
    type === "viewProfile"
  ) {
    return getSequenceActionMeta(type as SequenceActionType)?.label || type;
  }
  return "👍 Like Post";
};
const slotPathToKey = (path: FlowPathPart[]) => path.join(".");
const delayLabel = (value: number, unit: DelayUnit = "days") => {
  if (value <= 0) return "No Delay";
  if (unit === "hours") return value === 1 ? "1 Hour" : `${value} Hours`;
  return value === 1 ? "1 Day" : `${value} Days`;
};

const normalizeDelayUnit = (unit: unknown): DelayUnit =>
  unit === "hours" ? "hours" : "days";

const delayToHours = (value: number, unit: DelayUnit) =>
  unit === "hours" ? value : value * 24;

const hoursToDelay = (hours: number): { delayDays: number; delayUnit: DelayUnit } => {
  if (hours <= 0) return { delayDays: 0, delayUnit: "days" };
  if (hours % 24 === 0) return { delayDays: hours / 24, delayUnit: "days" };
  return { delayDays: hours, delayUnit: "hours" };
};

const slotWidth = (slot: FlowSlot): number => {
  if (!slot.next) return CTA_W + 16 + END_W;
  return nodeWidth(slot.next);
};

const nodeWidth = (node: FlowNode): number => {
  if (node.type === "likePost" || node.type === "viewProfile")
    return Math.max(ACTION_W, slotWidth(node.next));
  if (isReplyBranchNode(node)) {
    const left = slotWidth(node.notReplied);
    const right = node.replied.next ? slotWidth(node.replied) : 170;
    return Math.max(ACTION_W, left + X_GAP + right);
  }
  const left =
    node.type === "ifConnection"
      ? slotWidth(node.notConnected)
      : node.type === "ifOpenProfile"
        ? slotWidth(node.notOpenProfile)
        : slotWidth(node.notAccepted);
  const right =
    node.type === "ifConnection"
      ? slotWidth(node.connected)
      : node.type === "ifOpenProfile"
        ? slotWidth(node.openProfile)
        : slotWidth(node.accepted);
  return Math.max(ACTION_W, left + X_GAP + right);
};

function updateSlot(root: FlowNode | null, path: FlowPathPart[], updater: (slot: FlowSlot) => FlowSlot): FlowNode | null {
  if (!root || path.length === 0) return root;
  const [head, ...rest] = path;

  if (root.type === "likePost" || root.type === "viewProfile") {
    if (head !== "next") return root;
    const current = root.next;
    if (rest.length === 0) return { ...root, next: updater(current) };
    return { ...root, next: { ...current, next: updateSlot(current.next, rest, updater) } };
  }

  if (root.type === "ifConnection") {
    if (head !== "notConnected" && head !== "connected") return root;
    const current = root[head];
    if (rest.length === 0) return { ...root, [head]: updater(current) };
    return { ...root, [head]: { ...current, next: updateSlot(current.next, rest, updater) } };
  }

  if (root.type === "ifOpenProfile") {
    if (head !== "notOpenProfile" && head !== "openProfile") return root;
    const current = root[head];
    if (rest.length === 0) return { ...root, [head]: updater(current) };
    return { ...root, [head]: { ...current, next: updateSlot(current.next, rest, updater) } };
  }

  if (isReplyBranchNode(root)) {
    if (head !== "notReplied" && head !== "replied") return root;
    const current = root[head];
    if (rest.length === 0) return { ...root, [head]: updater(current) };
    return { ...root, [head]: { ...current, next: updateSlot(current.next, rest, updater) } };
  }

  if (head !== "notAccepted" && head !== "accepted") return root;
  const current = root[head];
  if (rest.length === 0) return { ...root, [head]: updater(current) };
  return { ...root, [head]: { ...current, next: updateSlot(current.next, rest, updater) } };
}

function getContinuationForDeletion(node: FlowNode): {
  next: FlowNode | null;
  delayDays: number;
  delayUnit: DelayUnit;
  ended: boolean;
} {
  const hasContent = (slot: FlowSlot) => Boolean(slot.next || slot.ended);

  if (node.type === "likePost" || node.type === "viewProfile") {
    return {
      next: node.next.next,
      delayDays: node.next.delayDays,
      delayUnit: normalizeDelayUnit(node.next.delayUnit),
      ended: node.next.ended,
    };
  }

  if (isReplyBranchNode(node)) {
    const slot = hasContent(node.replied) ? node.replied : hasContent(node.notReplied) ? node.notReplied : node.replied;
    return {
      next: slot.next,
      delayDays: slot.delayDays,
      delayUnit: normalizeDelayUnit(slot.delayUnit),
      ended: slot.ended,
    };
  }

  if (node.type === "sendConnectionRequest") {
    // When user added actions, they land in either `accepted` or `notAccepted`.
    // Prefer whichever branch has content to keep "splice" behavior intuitive.
    const slot = hasContent(node.accepted) ? node.accepted : hasContent(node.notAccepted) ? node.notAccepted : node.accepted;
    return {
      next: slot.next,
      delayDays: slot.delayDays,
      delayUnit: normalizeDelayUnit(slot.delayUnit),
      ended: slot.ended,
    };
  }

  if (node.type === "ifConnection") {
    const slot = hasContent(node.connected) ? node.connected : node.notConnected;
    return {
      next: slot.next,
      delayDays: slot.delayDays,
      delayUnit: normalizeDelayUnit(slot.delayUnit),
      ended: slot.ended,
    };
  }

  if (node.type === "ifOpenProfile") {
    const slot = hasContent(node.openProfile) ? node.openProfile : node.notOpenProfile;
    return {
      next: slot.next,
      delayDays: slot.delayDays,
      delayUnit: normalizeDelayUnit(slot.delayUnit),
      ended: slot.ended,
    };
  }

  // Like Post / Visit are the only linear types; others handled above.
  return { next: null, delayDays: 0, delayUnit: "days", ended: false };
}

function parseActionPathKey(key: string): FlowPathPart[] {
  return key
    .split(".")
    .map((p) => p as FlowPathPart)
    .filter(Boolean);
}

type ActionCardNodeData = {
  label: string;
  onEdit: () => void;
  onDelete: () => void;
};

function ActionCardNode({ data }: { data: ActionCardNodeData }) {
  return (
    <div
      className="flex h-full w-full items-center justify-between gap-3 rounded-[12px] border border-light-grey bg-white px-3 py-2 shadow-sm"
      onClick={data.onEdit}
      role="button"
      tabIndex={0}
    >
      {/* Required for ReactFlow edges to render with this custom node type. */}
      <Handle type="target" position={Position.Top} style={{ opacity: 0 }} />
      <Handle type="source" position={Position.Bottom} style={{ opacity: 0 }} />
      <span className="text-md font-medium text-dark-black">{data.label}</span>
      <button
        type="button"
        aria-label="Delete action"
        className="inline-flex h-6 w-6 items-center justify-center rounded hover:bg-background-grey"
        onClick={(e) => {
          e.stopPropagation();
          data.onDelete();
        }}
      >
        ×
      </button>
    </div>
  );
}

function getActionTypesForPath(root: FlowNode | null, path: FlowPathPart[] | null): Set<FlowActionType> {
  const used = new Set<FlowActionType>();
  if (!root) return used;

  let current: FlowNode | null = root;
  used.add(current.type);
  const steps = path ?? [];

  for (const step of steps) {
    if (!current) break;

    if (current.type === "ifConnection" && (step === "notConnected" || step === "connected")) {
      current = current[step].next;
    } else if (current.type === "ifOpenProfile" && (step === "notOpenProfile" || step === "openProfile")) {
      current = current[step].next;
    } else if (current.type === "sendConnectionRequest" && (step === "notAccepted" || step === "accepted")) {
      current = current[step].next;
    } else if (isReplyBranchNode(current) && (step === "notReplied" || step === "replied")) {
      current = current[step].next;
    } else if ((current.type === "likePost" || current.type === "viewProfile") && step === "next") {
      current = current.next.next;
    } else {
      break;
    }

    if (current) used.add(current.type);
  }

  return used;
}

function getAllActionTypes(root: FlowNode | null): Set<FlowActionType> {
  const used = new Set<FlowActionType>();
  if (!root) return used;

  const walk = (node: FlowNode | null) => {
    if (!node) return;
    used.add(node.type);

    if (node.type === "ifConnection") {
      walk(node.notConnected.next);
      walk(node.connected.next);
      return;
    }
    if (node.type === "ifOpenProfile") {
      walk(node.notOpenProfile.next);
      walk(node.openProfile.next);
      return;
    }
    if (node.type === "sendConnectionRequest") {
      walk(node.notAccepted.next);
      walk(node.accepted.next);
      return;
    }
    if (isReplyBranchNode(node)) {
      walk(node.notReplied.next);
      walk(node.replied.next);
      return;
    }

    walk(node.next.next);
  };

  walk(root);
  return used;
}

type ActionPreviewItem = {
  path: string;
  type: FlowActionType;
  inheritedDelayDays: number;
  inheritedDelayUnit: DelayUnit;
};

type LegacyActionPreviewItem = {
  type: "invitation" | "visit" | "visitWithDelay" | "message" | "messageWithDelay" | "emailSent" | "emailSentWithDelay";
  delay?: number;
  delayUnit?: DelayUnit;
  customMessage?: string;
  message?: {
    subject?: string;
    body?: string;
  };
  fallbackMessage?: string;
  withdrawAfterDays?: number;
  isAiMessageEnabled?: boolean;
  messageVariations?: MessageVariation[];
  _debugPath: string;
};

type LegacySequenceFallback = {
  showLegacyActionsButton: boolean;
  readonlyActions: LegacyActionPreviewItem[];
};

const normalizeLegacyActionType = (value: unknown): LegacyActionPreviewItem["type"] | null => {
  const normalized = String(value || "").trim();
  if (
    normalized === "invitation" ||
    normalized === "visit" ||
    normalized === "visitWithDelay" ||
    normalized === "message" ||
    normalized === "messageWithDelay" ||
    normalized === "emailSent" ||
    normalized === "emailSentWithDelay"
  ) {
    return normalized;
  }
  return null;
};

const parseLegacyDelay = (value: unknown): number | undefined => {
  if (typeof value === "number" && Number.isFinite(value) && value > 0) {
    return value;
  }
  if (typeof value === "string") {
    const parsed = Number(value.trim());
    if (Number.isFinite(parsed) && parsed > 0) {
      return parsed;
    }
  }
  return undefined;
};

const resolveLegacySequenceFallback = (campaign: any): LegacySequenceFallback => {
  const hasHydratedGraph = Boolean(campaign?.sequenceRehydration?.sequenceGraph?.rootNode);
  const isCustomSchedule = String(campaign?.scheduleMode || "").toUpperCase() === "CAMPAIGN_CUSTOM";
  const readonlyActions: LegacyActionPreviewItem[] = Array.isArray(campaign?.actions)
    ? campaign.actions
        .map((action: any, index: number) => {
          const actionType = normalizeLegacyActionType(action?.type);
          if (!actionType) return null;
          return {
            type: actionType,
            delay: parseLegacyDelay(action?.delay),
            delayUnit: action?.delayUnit === "hours" ? "hours" : "days",
            customMessage: typeof action?.customMessage === "string" ? action.customMessage : undefined,
            message: action?.message && typeof action.message === "object" ? action.message : undefined,
            fallbackMessage:
              typeof action?.fallbackMessage === "string" ? action.fallbackMessage : undefined,
            withdrawAfterDays:
              typeof action?.withdrawAfterDays === "number" ? action.withdrawAfterDays : undefined,
            isAiMessageEnabled: Boolean(action?.isAiMessageEnabled),
            messageVariations: Array.isArray(action?.messageVariations)
              ? action.messageVariations
              : undefined,
            _debugPath: `legacy.${index}`,
          };
        })
        .filter(Boolean) as LegacyActionPreviewItem[]
    : [];

  return {
    showLegacyActionsButton:
      !hasHydratedGraph && !isCustomSchedule && readonlyActions.length > 0,
    readonlyActions,
  };
};

const legacyActionTitle = (action: LegacyActionPreviewItem["type"]) => {
  if (action === "invitation") return "Invitation";
  if (action === "visit" || action === "visitWithDelay") return "Visit Profile";
  if (action === "emailSent" || action === "emailSentWithDelay") return "Email";
  return "Message";
};

function collectActionPreview(
  node: FlowNode | null,
  path: string,
  inheritedDelayDays: number,
  inheritedDelayUnit: DelayUnit,
  output: ActionPreviewItem[]
) {
  if (!node) return;

  output.push({
    path,
    type: node.type,
    inheritedDelayDays,
    inheritedDelayUnit,
  });

  if (node.type === "ifConnection") {
    collectActionPreview(
      node.notConnected.next,
      `${path}.notConnected`,
      node.notConnected.delayDays,
      normalizeDelayUnit(node.notConnected.delayUnit),
      output
    );
    collectActionPreview(
      node.connected.next,
      `${path}.connected`,
      node.connected.delayDays,
      normalizeDelayUnit(node.connected.delayUnit),
      output
    );
    return;
  }

  if (node.type === "ifOpenProfile") {
    collectActionPreview(
      node.notOpenProfile.next,
      `${path}.notOpenProfile`,
      node.notOpenProfile.delayDays,
      normalizeDelayUnit(node.notOpenProfile.delayUnit),
      output
    );
    collectActionPreview(
      node.openProfile.next,
      `${path}.openProfile`,
      node.openProfile.delayDays,
      normalizeDelayUnit(node.openProfile.delayUnit),
      output
    );
    return;
  }

  if (node.type === "sendConnectionRequest") {
    collectActionPreview(
      node.notAccepted.next,
      `${path}.notAccepted`,
      node.notAccepted.delayDays,
      normalizeDelayUnit(node.notAccepted.delayUnit),
      output
    );
    collectActionPreview(
      node.accepted.next,
      `${path}.accepted`,
      node.accepted.delayDays,
      normalizeDelayUnit(node.accepted.delayUnit),
      output
    );
    return;
  }

  if (isReplyBranchNode(node)) {
    collectActionPreview(
      node.notReplied.next,
      `${path}.notReplied`,
      node.notReplied.delayDays,
      normalizeDelayUnit(node.notReplied.delayUnit),
      output
    );
    collectActionPreview(
      node.replied.next,
      `${path}.replied`,
      node.replied.delayDays,
      normalizeDelayUnit(node.replied.delayUnit),
      output
    );
    return;
  }

  collectActionPreview(
    node.next.next,
    `${path}.next`,
    node.next.delayDays,
    normalizeDelayUnit(node.next.delayUnit),
    output
  );
}

function firstVariationMessage(variations: MessageVariation[] | undefined) {
  if (!Array.isArray(variations) || variations.length === 0) return "";
  return (variations[0]?.message || "").trim();
}

function collectLegacyActionsPayload(
  node: FlowNode | null,
  path: string,
  inheritedDelayDays: number,
  inheritedDelayUnit: DelayUnit,
  output: LegacyActionPreviewItem[]
) {
  if (!node) return;

  if (node.type === "sendConnectionRequest") {
    output.push({
      type: "invitation",
      customMessage: firstVariationMessage(node.config.messageVariations),
      fallbackMessage: node.config.fallbackMessage,
      withdrawAfterDays: node.config.withdrawAfterDays,
      isAiMessageEnabled: node.config.isAiMessageEnabled,
      messageVariations: node.config.messageVariations,
      _debugPath: path,
    });

    collectLegacyActionsPayload(
      node.notAccepted.next,
      `${path}.notAccepted`,
      node.notAccepted.delayDays,
      normalizeDelayUnit(node.notAccepted.delayUnit),
      output
    );
    collectLegacyActionsPayload(
      node.accepted.next,
      `${path}.accepted`,
      node.accepted.delayDays,
      normalizeDelayUnit(node.accepted.delayUnit),
      output
    );
    return;
  }

  if (node.type === "viewProfile") {
    // Legacy-compatible behavior: delay applies to the NEXT action, not current one.
    // So a delay configured on this node's outgoing slot should not delay this visit.
    const rawVisitDelay = inheritedDelayDays > 0 ? inheritedDelayDays : 0;
    const effectiveVisitDelayUnit = inheritedDelayUnit;

    output.push(
      rawVisitDelay > 0
        ? {
            type: "visitWithDelay",
            delay: rawVisitDelay,
            delayUnit: effectiveVisitDelayUnit,
            _debugPath: path,
          }
        : {
            type: "visit",
            _debugPath: path,
          }
    );
    collectLegacyActionsPayload(
      node.next.next,
      `${path}.next`,
      node.next.delayDays,
      normalizeDelayUnit(node.next.delayUnit),
      output
    );
    return;
  }

  if (isReplyBranchNode(node)) {
    const messageBody = firstVariationMessage(node.config.messageVariations);
    const rawMessageDelay = inheritedDelayDays > 0 ? inheritedDelayDays : 0;
    const effectiveMessageDelayUnit = inheritedDelayUnit;
    const isEmail = node.type === "sendEmail";
    const delayedType = isEmail ? "emailSentWithDelay" : "messageWithDelay";
    const immediateType = isEmail ? "emailSent" : "message";

    output.push(
      rawMessageDelay > 0
        ? {
            type: delayedType,
            delay: rawMessageDelay,
            delayUnit: effectiveMessageDelayUnit,
            message: {
              subject: node.config.subject || "",
              body: messageBody,
            },
            fallbackMessage: node.config.fallbackMessage,
            isAiMessageEnabled: node.config.isAiMessageEnabled,
            messageVariations: node.config.messageVariations,
            _debugPath: path,
          }
        : {
            type: immediateType,
            message: {
              subject: node.config.subject || "",
              body: messageBody,
            },
            fallbackMessage: node.config.fallbackMessage,
            isAiMessageEnabled: node.config.isAiMessageEnabled,
            messageVariations: node.config.messageVariations,
            _debugPath: path,
          }
    );

    collectLegacyActionsPayload(
      node.notReplied.next,
      `${path}.notReplied`,
      node.notReplied.delayDays,
      normalizeDelayUnit(node.notReplied.delayUnit),
      output
    );
    collectLegacyActionsPayload(
      node.replied.next,
      `${path}.replied`,
      node.replied.delayDays,
      normalizeDelayUnit(node.replied.delayUnit),
      output
    );
    return;
  }

  if (node.type === "ifConnection") {
    collectLegacyActionsPayload(
      node.notConnected.next,
      `${path}.notConnected`,
      node.notConnected.delayDays,
      normalizeDelayUnit(node.notConnected.delayUnit),
      output
    );
    collectLegacyActionsPayload(
      node.connected.next,
      `${path}.connected`,
      node.connected.delayDays,
      normalizeDelayUnit(node.connected.delayUnit),
      output
    );
    return;
  }

  if (node.type === "ifOpenProfile") {
    collectLegacyActionsPayload(
      node.notOpenProfile.next,
      `${path}.notOpenProfile`,
      node.notOpenProfile.delayDays,
      normalizeDelayUnit(node.notOpenProfile.delayUnit),
      output
    );
    collectLegacyActionsPayload(
      node.openProfile.next,
      `${path}.openProfile`,
      node.openProfile.delayDays,
      normalizeDelayUnit(node.openProfile.delayUnit),
      output
    );
    return;
  }

  collectLegacyActionsPayload(
    node.next.next,
    `${path}.next`,
    node.next.delayDays,
    normalizeDelayUnit(node.next.delayUnit),
    output
  );
}

function SequenceCanvas() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { run: runContinue, isPending: isContinuing } = useAsyncAction();
  const { owner, currentUser, currentWorkspace } = useSelector(ownerSelector);
  const { templates } = useSelector(templateSelector);
  const flowState = (location.state as CampaignFlowState | null) || {};
  const campaignId = resolveCampaignIdFromStateOrSearch(flowState, searchParams.get("campaignId"));
  const resolvedScope = React.useMemo(
    () =>
      resolveFlowScope({
        flowState,
        owner,
        currentUser,
        currentWorkspace,
      }),
    [flowState, owner, currentUser, currentWorkspace]
  );
  const reactFlow = useReactFlow();

  const [zoom, setZoom] = React.useState(100);
  const [rootNode, setRootNode] = React.useState<FlowNode | null>(null);
  const [nodes, setNodes, onNodesChange] = useNodesState<Node<GraphNodeData>>([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState<Edge>([]);

  const [isMenuOpen, setIsMenuOpen] = React.useState(false);
  const [menuPos, setMenuPos] = React.useState({ x: 12, y: 96 });
  const [targetPath, setTargetPath] = React.useState<FlowPathPart[] | null>(null);
  const [pendingType, setPendingType] = React.useState<FlowActionType | null>(null);
  const [isEditingRootAction, setIsEditingRootAction] = React.useState(false);

  const [isSidebarOpen, setIsSidebarOpen] = React.useState(false);
  const [editingConnectionRequestConfig, setEditingConnectionRequestConfig] =
    React.useState<ConnectionRequestConfig | null>(null);
  const [isSendInMailSidebarOpen, setIsSendInMailSidebarOpen] = React.useState(false);
  const [editingSendInMailConfig, setEditingSendInMailConfig] =
    React.useState<SendInMailConfig | null>(null);
  const [editingDelayPath, setEditingDelayPath] = React.useState<FlowPathPart[] | null>(null);
  const [isLegacyActionsModalOpen, setIsLegacyActionsModalOpen] = React.useState(false);
  const [showLegacyActionsButton, setShowLegacyActionsButton] = React.useState(false);
  const [legacyReadonlyActions, setLegacyReadonlyActions] = React.useState<LegacyActionPreviewItem[]>([]);
  const [hydrationDone, setHydrationDone] = React.useState(() => !campaignId);
  const [startFromScratch, setStartFromScratch] = React.useState(false);
  const [appliedTemplateId, setAppliedTemplateId] = React.useState<string | null>(null);
  const [emailChannelVerified, setEmailChannelVerified] = React.useState(false);
  const menuAnchorRef = React.useRef<HTMLDivElement | null>(null);
  const graphRef = React.useRef<HTMLDivElement | null>(null);
  const menuRef = React.useRef<HTMLDivElement | null>(null);

  // Prevent draft rehydration from overriding the user's live edits (e.g. deleting the last action
  // makes `rootNode` null, which would otherwise trigger re-fetch from DB).
  const hasUserEditedGraphRef = React.useRef(false);
  const hasUserPickedTemplateRef = React.useRef(false);

  React.useEffect(() => {
    hasUserEditedGraphRef.current = false;
    hasUserPickedTemplateRef.current = false;
    setHydrationDone(!campaignId);
    setStartFromScratch(false);
    setAppliedTemplateId(null);
  }, [campaignId]);

  React.useEffect(() => {
    let cancelled = false;
    const loadEmailChannel = async () => {
      if (!resolvedScope.ownerId && !resolvedScope.userId && !resolvedScope.workspaceId) return;
      try {
        const status = await fetchEmailIntegration({
          ownerId: resolvedScope.ownerId,
          userId: resolvedScope.userId,
          workspaceId: resolvedScope.workspaceId,
        });
        if (!cancelled) setEmailChannelVerified(Boolean(status.verified));
      } catch {
        if (!cancelled) setEmailChannelVerified(false);
      }
    };
    void loadEmailChannel();
    return () => {
      cancelled = true;
    };
  }, [resolvedScope.ownerId, resolvedScope.userId, resolvedScope.workspaceId]);

  React.useEffect(() => {
    if (!campaignId) return;
    let cancelled = false;
    const hydrateAppliedTemplate = async () => {
      try {
        const draftResponse = await fetchCampaignDraft({
          campaignId,
          ownerId: resolvedScope.ownerId,
          userId: resolvedScope.userId,
          workspaceId: resolvedScope.workspaceId,
        });
        if (cancelled || hasUserPickedTemplateRef.current) return;
        const storedId = draftResponse?.campaign?.sequenceTemplateId;
        setAppliedTemplateId(storedId ? String(storedId) : null);
      } catch {
        // Keep local picker state if draft fetch fails.
      }
    };
    void hydrateAppliedTemplate();
    return () => {
      cancelled = true;
    };
  }, [
    campaignId,
    resolvedScope.ownerId,
    resolvedScope.userId,
    resolvedScope.workspaceId,
  ]);

  React.useEffect(() => {
    const existingRootNode = (flowState.stepThreeRehydration as any)?.sequenceGraph?.rootNode;
    if (existingRootNode && typeof existingRootNode === "object") {
      setRootNode(existingRootNode as FlowNode);
      setStartFromScratch(true);
      setHydrationDone(true);
    }
  }, [flowState.stepThreeRehydration]);

  React.useEffect(() => {
    const hydrateSequenceFromDraft = async () => {
      if (!campaignId) {
        setHydrationDone(true);
        return;
      }
      if (rootNode) {
        setHydrationDone(true);
        return;
      }
      if ((flowState.stepThreeRehydration as any)?.sequenceGraph?.rootNode) {
        setHydrationDone(true);
        return;
      }
      if (hasUserEditedGraphRef.current) {
        setHydrationDone(true);
        return;
      }
      try {
        const draftResponse = await fetchCampaignDraft({
          campaignId,
          ownerId: resolvedScope.ownerId,
          userId: resolvedScope.userId,
          workspaceId: resolvedScope.workspaceId,
        });
        const campaign = draftResponse?.campaign;
        const hydratedRootNode = campaign?.sequenceRehydration?.sequenceGraph?.rootNode;
        if (hydratedRootNode && typeof hydratedRootNode === "object") {
          setRootNode(hydratedRootNode as FlowNode);
          setStartFromScratch(true);
        }
        const legacyFallback = resolveLegacySequenceFallback(campaign);
        setShowLegacyActionsButton(legacyFallback.showLegacyActionsButton);
        setLegacyReadonlyActions(legacyFallback.readonlyActions);
      } catch (error) {
        console.log(error);
      } finally {
        setHydrationDone(true);
      }
    };
    void hydrateSequenceFromDraft();
  }, [
    campaignId,
    resolvedScope.ownerId,
    resolvedScope.userId,
    resolvedScope.workspaceId,
    flowState.stepThreeRehydration,
    rootNode,
  ]);

  const rebuildGraph = React.useCallback(() => {
    if (!rootNode) {
      setNodes([]);
      setEdges([]);
      return;
    }

    const builtNodes: Node<GraphNodeData>[] = [];
    const builtEdges: Edge[] = [];

    const renderSlotWithoutDelay = (
      slot: FlowSlot,
      label: string,
      path: FlowPathPart[],
      x: number,
      y: number,
      parentId: string
    ): void => {
      const key = slotPathToKey(path);
      const branchId = `branch:${key}`;
      builtNodes.push({
        id: branchId,
        position: { x: x - BRANCH_W / 2, y },
        data: { label, kind: "branch" },
        style: { ...textOnlyStyle, width: BRANCH_W, fontWeight: 600 },
      });
      builtEdges.push({
        id: `e-${parentId}-${branchId}`,
        source: parentId,
        target: branchId,
        style: { stroke: "#d8d8d8", strokeWidth: 2 },
      });

      if (slot.next) {
        renderAction(slot.next, x, y + Y_GAP + 40, branchId, path);
        return;
      }

      if (slot.ended) {
        const endedId = `ended:${key}`;
        builtNodes.push({
          id: endedId,
          position: { x: x - 84, y: y + Y_GAP + 34 },
          data: { label: "✓ End   ×", kind: "end" },
          style: { ...textOnlyStyle, width: 170, fontWeight: 700, color: "var(--color-brand-blue)" },
        });
        builtEdges.push({
          id: `e-${branchId}-${endedId}`,
          source: branchId,
          target: endedId,
          style: { stroke: "#d8d8d8", strokeWidth: 2 },
        });
        return;
      }

      const ctaId = `add:${key}`;
      const endId = `end:${key}`;
      const rowX = x - (CTA_W + 16 + END_W) / 2;
      builtNodes.push({
        id: ctaId,
        position: { x: rowX, y: y + Y_GAP + 20 },
        data: { label: "+ Add action", kind: "cta" },
        style: { ...ctaStyle, width: CTA_W },
      });
      builtNodes.push({
        id: endId,
        position: { x: rowX + CTA_W + 16, y: y + Y_GAP + 20 },
        data: { label: "End", kind: "end" },
        style: { ...endStyle, width: END_W },
      });
      builtEdges.push({
        id: `e-${branchId}-${ctaId}`,
        source: branchId,
        target: ctaId,
        style: { stroke: "#d8d8d8", strokeWidth: 2 },
      });
      builtEdges.push({
        id: `e-${branchId}-${endId}`,
        source: branchId,
        target: endId,
        style: { stroke: "#d8d8d8", strokeWidth: 2 },
      });
    };

    const renderSlot = (slot: FlowSlot, label: string, path: FlowPathPart[], x: number, y: number, parentId: string): void => {
      const key = slotPathToKey(path);
      const branchId = `branch:${key}`;
      const delayId = `delay:${key}`;
      builtNodes.push({ id: branchId, position: { x: x - BRANCH_W / 2, y }, data: { label, kind: "branch" }, style: { ...textOnlyStyle, width: BRANCH_W, fontWeight: 600 } });
      builtEdges.push({ id: `e-${parentId}-${branchId}`, source: parentId, target: branchId, style: { stroke: "#d8d8d8", strokeWidth: 2 } });

      builtNodes.push({
        id: delayId,
        position: { x: x - DELAY_W / 2, y: y + 52 },
        data: { label: delayLabel(slot.delayDays, normalizeDelayUnit(slot.delayUnit)), kind: "delay" },
        style: { ...cardStyle, width: DELAY_W },
      });
      builtEdges.push({ id: `e-${branchId}-${delayId}`, source: branchId, target: delayId, style: { stroke: "#d8d8d8", strokeWidth: 2 } });

      if (slot.next) {
        renderAction(slot.next, x, y + Y_GAP + 60, delayId, path);
        return;
      }

      if (slot.ended) {
        const endedId = `ended:${key}`;
        builtNodes.push({ id: endedId, position: { x: x - 84, y: y + Y_GAP + 54 }, data: { label: "✓ End   ×", kind: "end" }, style: { ...textOnlyStyle, width: 170, fontWeight: 700, color: "var(--color-brand-blue)" } });
        builtEdges.push({ id: `e-${delayId}-${endedId}`, source: delayId, target: endedId, style: { stroke: "#d8d8d8", strokeWidth: 2 } });
        return;
      }

      const ctaId = `add:${key}`;
      const endId = `end:${key}`;
      const rowX = x - (CTA_W + 16 + END_W) / 2;
      builtNodes.push({ id: ctaId, position: { x: rowX, y: y + Y_GAP + 40 }, data: { label: "+ Add action", kind: "cta" }, style: { ...ctaStyle, width: CTA_W } });
      builtNodes.push({ id: endId, position: { x: rowX + CTA_W + 16, y: y + Y_GAP + 40 }, data: { label: "End", kind: "end" }, style: { ...endStyle, width: END_W } });
      builtEdges.push({ id: `e-${delayId}-${ctaId}`, source: delayId, target: ctaId, style: { stroke: "#d8d8d8", strokeWidth: 2 } });
      builtEdges.push({ id: `e-${delayId}-${endId}`, source: delayId, target: endId, style: { stroke: "#d8d8d8", strokeWidth: 2 } });
    };

    const renderAction = (
      node: FlowNode,
      x: number,
      y: number,
      parentId?: string,
      basePath: FlowPathPart[] = []
    ): void => {
      const actionKey = basePath.length > 0 ? slotPathToKey(basePath) : "root";
      const actionId = `action:${actionKey}`;

      const actionBasePath = basePath;
      const isRootAction = actionBasePath.length === 0;

      const handleEdit = () => {
        if (node.type === "sendConnectionRequest") {
          setPendingType("sendConnectionRequest");
          setTargetPath(isRootAction ? null : actionBasePath);
          setIsEditingRootAction(isRootAction);
          setEditingConnectionRequestConfig((node as SendConnectionRequestNode).config);
          setEditingSendInMailConfig(null);
          setIsSidebarOpen(true);
          setIsSendInMailSidebarOpen(false);
          return;
        }

        if (isReplyBranchNode(node)) {
          setPendingType(node.type);
          setTargetPath(isRootAction ? null : actionBasePath);
          setIsEditingRootAction(isRootAction);
          setEditingSendInMailConfig((node as SendInMailNode).config);
          setEditingConnectionRequestConfig(null);
          setIsSendInMailSidebarOpen(true);
          setIsSidebarOpen(false);
          return;
        }

        toast.info("No editable settings for this action.");
      };

      const handleDelete = () => {
        hasUserEditedGraphRef.current = true;
        setIsEditingRootAction(false);
        setPendingType(null);
        setIsSidebarOpen(false);
        setIsSendInMailSidebarOpen(false);
        setEditingConnectionRequestConfig(null);
        setEditingSendInMailConfig(null);

        if (!rootNode) return;

        if (isRootAction) {
          setRootNode(() => {
            const cont = getContinuationForDeletion(rootNode);
            return cont.next;
          });
          return;
        }

        setRootNode((prev) =>
          updateSlot(prev, actionBasePath, (slot) => {
            const toDelete = slot.next;
            if (!toDelete) return slot;
            const cont = getContinuationForDeletion(toDelete);
            const mergedDelayHours =
              delayToHours(slot.delayDays || 0, normalizeDelayUnit(slot.delayUnit)) +
              delayToHours(cont.delayDays || 0, cont.delayUnit);
            const mergedDelay = hoursToDelay(mergedDelayHours);
            return {
              ...slot,
              ...mergedDelay,
              ended: cont.ended,
              next: cont.next,
            };
          })
        );
      };

      builtNodes.push({
        id: actionId,
        type: "actionCard",
        position: { x: x - ACTION_W / 2, y },
        data: {
          label: nodeLabel(node.type),
          kind: "action",
          onEdit: handleEdit,
          onDelete: handleDelete,
        },
        style: { ...cardStyle, width: ACTION_W },
      } as any);
      if (parentId) builtEdges.push({ id: `e-${parentId}-${actionId}`, source: parentId, target: actionId, style: { stroke: "#d8d8d8", strokeWidth: 2 } });

      if (node.type === "likePost" || node.type === "viewProfile") {
        renderSlot(node.next, "Next", [...basePath, "next"], x, y + Y_GAP, actionId);
        return;
      }

      if (node.type === "sendConnectionRequest") {
        const invitationNode = node as SendConnectionRequestNode;
        const acceptedHasContent = Boolean(invitationNode.accepted.next || invitationNode.accepted.ended);
        const notAcceptedHasContent = Boolean(invitationNode.notAccepted.next || invitationNode.notAccepted.ended);

        // UI simplification: render only one continuation slot under the Invitation action.
        // We keep the underlying paths (`accepted` / `notAccepted`) unchanged so the backend payload
        // generation remains compatible.
        const slotPart: FlowPathPart = acceptedHasContent
          ? "accepted"
          : notAcceptedHasContent
            ? "notAccepted"
            : "accepted";
        const slot = slotPart === "accepted" ? invitationNode.accepted : invitationNode.notAccepted;
        const slotPath = [...basePath, slotPart] as FlowPathPart[];
        const key = slotPathToKey(slotPath);
        const delayId = `delay:${key}`;

        builtNodes.push({
          id: delayId,
          position: { x: x - DELAY_W / 2, y: y + Y_GAP },
          data: { label: delayLabel(slot.delayDays, normalizeDelayUnit(slot.delayUnit)), kind: "delay" },
          style: { ...cardStyle, width: DELAY_W },
        });
        builtEdges.push({
          id: `e-${actionId}-${delayId}`,
          source: actionId,
          target: delayId,
          style: { stroke: "#d8d8d8", strokeWidth: 2 },
        });

        if (slot.next) {
          renderAction(slot.next, x, y + Y_GAP + 150, delayId, slotPath);
          return;
        }

        if (slot.ended) {
          const endedId = `ended:${key}`;
          builtNodes.push({
            id: endedId,
            position: { x: x - 84, y: y + Y_GAP + 146 },
            data: { label: "✓ End   ×", kind: "end" },
            style: { ...textOnlyStyle, width: 170, fontWeight: 700, color: "var(--color-brand-blue)" },
          });
          builtEdges.push({
            id: `e-${delayId}-${endedId}`,
            source: delayId,
            target: endedId,
            style: { stroke: "#d8d8d8", strokeWidth: 2 },
          });
          return;
        }

        const ctaId = `add:${key}`;
        const endId = `end:${key}`;
        const rowX = x - (CTA_W + 16 + END_W) / 2;

        builtNodes.push({
          id: ctaId,
          position: { x: rowX, y: y + Y_GAP + 132 },
          data: { label: "+ Add action", kind: "cta" },
          style: { ...ctaStyle, width: CTA_W },
        });
        builtNodes.push({
          id: endId,
          position: { x: rowX + CTA_W + 16, y: y + Y_GAP + 132 },
          data: { label: "End", kind: "end" },
          style: { ...endStyle, width: END_W },
        });
        builtEdges.push({
          id: `e-${delayId}-${ctaId}`,
          source: delayId,
          target: ctaId,
          style: { stroke: "#d8d8d8", strokeWidth: 2 },
        });
        builtEdges.push({
          id: `e-${delayId}-${endId}`,
          source: delayId,
          target: endId,
          style: { stroke: "#d8d8d8", strokeWidth: 2 },
        });
        return;
      }

      if (isReplyBranchNode(node)) {
        const repliedHasActionContent = Boolean(node.replied.next);
        const notRepliedHasContent = Boolean(node.notReplied.next || node.notReplied.ended);

        // UI simplification: render only one continuation slot under Message.
        // Keep `notReplied` / `replied` path keys for payload compatibility.
        const slotPart: FlowPathPart = notRepliedHasContent
          ? "notReplied"
          : repliedHasActionContent
            ? "replied"
            : "notReplied";
        const slot = slotPart === "notReplied" ? node.notReplied : node.replied;
        const slotPath = [...basePath, slotPart] as FlowPathPart[];
        const key = slotPathToKey(slotPath);

        const delayId = `delay:${key}`;
        builtNodes.push({
          id: delayId,
          position: { x: x - DELAY_W / 2, y: y + Y_GAP },
          data: { label: delayLabel(slot.delayDays, normalizeDelayUnit(slot.delayUnit)), kind: "delay" },
          style: { ...cardStyle, width: DELAY_W },
        });
        builtEdges.push({
          id: `e-${actionId}-${delayId}`,
          source: actionId,
          target: delayId,
          style: { stroke: "#d8d8d8", strokeWidth: 2 },
        });

        if (slot.next) {
          renderAction(slot.next, x, y + Y_GAP + 150, delayId, slotPath);
          return;
        }

        if (slot.ended) {
          const endedId = `ended:${key}`;
          builtNodes.push({
            id: endedId,
            position: { x: x - 84, y: y + Y_GAP + 146 },
            data: { label: "✓ End   ×", kind: "end" },
            style: { ...textOnlyStyle, width: 170, fontWeight: 700, color: "var(--color-brand-blue)" },
          });
          builtEdges.push({
            id: `e-${delayId}-${endedId}`,
            source: delayId,
            target: endedId,
            style: { stroke: "#d8d8d8", strokeWidth: 2 },
          });
          return;
        }

        const ctaId = `add:${key}`;
        const endId = `end:${key}`;
        const rowX = x - (CTA_W + 16 + END_W) / 2;
        builtNodes.push({
          id: ctaId,
          position: { x: rowX, y: y + Y_GAP + 132 },
          data: { label: "+ Add action", kind: "cta" },
          style: { ...ctaStyle, width: CTA_W },
        });
        builtNodes.push({
          id: endId,
          position: { x: rowX + CTA_W + 16, y: y + Y_GAP + 132 },
          data: { label: "End", kind: "end" },
          style: { ...endStyle, width: END_W },
        });
        builtEdges.push({
          id: `e-${delayId}-${ctaId}`,
          source: delayId,
          target: ctaId,
          style: { stroke: "#d8d8d8", strokeWidth: 2 },
        });
        builtEdges.push({
          id: `e-${delayId}-${endId}`,
          source: delayId,
          target: endId,
          style: { stroke: "#d8d8d8", strokeWidth: 2 },
        });
        return;
      }

      const isIfConnection = node.type === "ifConnection";
      const leftSlot = isIfConnection ? node.notConnected : node.notOpenProfile;
      const rightSlot = isIfConnection ? node.connected : node.openProfile;
      const leftLabel = isIfConnection ? "Not Connected" : "Not Open Profile";
      const rightLabel = isIfConnection ? "Connected" : "Open Profile";
      const leftPathHead: FlowPathPart = isIfConnection ? "notConnected" : "notOpenProfile";
      const rightPathHead: FlowPathPart = isIfConnection ? "connected" : "openProfile";

      const leftW = slotWidth(leftSlot);
      const rightW = slotWidth(rightSlot);
      const leftX = x - (rightW + X_GAP) / 2;
      const rightX = x + (leftW + X_GAP) / 2;

      renderSlot(leftSlot, leftLabel, [...basePath, leftPathHead], leftX, y + Y_GAP, actionId);
      renderSlot(rightSlot, rightLabel, [...basePath, rightPathHead], rightX, y + Y_GAP, actionId);
    };

    const startId = "root:start";
    builtNodes.push({ id: startId, position: { x: 480, y: 8 }, data: { label: "Campaign Start", kind: "root" }, style: { ...textOnlyStyle, width: 180, fontWeight: 700 } });
    renderAction(rootNode, 560, 78, startId);

    setNodes(builtNodes);
    setEdges(builtEdges);
  }, [rootNode, setEdges, setNodes]);

  React.useEffect(() => {
    rebuildGraph();
  }, [rebuildGraph]);

  React.useEffect(() => {
    reactFlow.setViewport({ x: 0, y: 0, zoom: zoom / 100 }, { duration: 0 });
  }, [reactFlow, zoom]);

  React.useEffect(() => {
    if (!isMenuOpen) return;
    const handleOutsideClick = (event: MouseEvent) => {
      if (!menuRef.current) return;
      const target = event.target as HTMLElement | null;
      if (target && menuRef.current.contains(target)) return;
      setIsMenuOpen(false);
    };
    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, [isMenuOpen]);

  const openMenuAt = (event: React.MouseEvent, path: FlowPathPart[] | null) => {
    const el = graphRef.current || menuAnchorRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const x = Math.max(8, Math.min(event.clientX - rect.left, rect.width - 392));
    const y = Math.max(8, event.clientY - rect.top + 8);
    setMenuPos({ x, y });
    setTargetPath(path);
    setIsMenuOpen(true);
  };

  const addSelectedAction = (option: ActionOption) => {
    if (option.id === "step-delay") {
      if (!targetPath) {
        setIsMenuOpen(false);
        return;
      }
      const isInvitationImmediateBranch =
        targetPath.length > 0 &&
        (targetPath[targetPath.length - 1] === "notAccepted" ||
          targetPath[targetPath.length - 1] === "accepted");
      if (isInvitationImmediateBranch) {
        toast.info("Delay is not supported directly after Invitation.");
        setIsMenuOpen(false);
        return;
      }
      setEditingDelayPath(targetPath);
      setIsMenuOpen(false);
      return;
    }

    if (!option.actionType) {
      console.log("[Sequence Action Pending Template]", option.id);
      setIsMenuOpen(false);
      return;
    }

    if (option.actionType === "sendEmail" && !emailChannelVerified) {
      toast.info(EMAIL_CHANNEL_REQUIRED_MESSAGE);
      setIsMenuOpen(false);
      return;
    }

    if (!targetPath) {
      if (rootNode) {
        toast.info("Root action already exists. Use '+ Add action' inside the graph to extend the sequence.");
        setIsMenuOpen(false);
        return;
      }

      if (option.actionType === "sendConnectionRequest" || isReplyBranchType(option.actionType)) {
        setPendingType(option.actionType);
        setIsEditingRootAction(false);
        if (option.actionType === "sendConnectionRequest") {
          setEditingConnectionRequestConfig(null);
          setEditingSendInMailConfig(null);
          setIsSidebarOpen(true);
        } else {
          setEditingSendInMailConfig(null);
          setEditingConnectionRequestConfig(null);
          setIsSendInMailSidebarOpen(true);
        }
      } else {
        setRootNode(makeNode(option.actionType));
      }
      setIsMenuOpen(false);
      return;
    }

    if (option.actionType === "sendConnectionRequest" || isReplyBranchType(option.actionType)) {
      setPendingType(option.actionType);
      setIsEditingRootAction(false);
      if (option.actionType === "sendConnectionRequest") {
        setEditingConnectionRequestConfig(null);
        setEditingSendInMailConfig(null);
        setIsSidebarOpen(true);
      } else {
        setEditingSendInMailConfig(null);
        setEditingConnectionRequestConfig(null);
        setIsSendInMailSidebarOpen(true);
      }
      setIsMenuOpen(false);
      return;
    }

    setRootNode((prev) => updateSlot(prev, targetPath, (slot) => ({ ...slot, ended: false, next: makeNode(option.actionType!) })));
    setIsMenuOpen(false);
  };

  const applyZoom = (next: number) => {
    const clamped = Math.max(20, Math.min(200, next));
    setZoom(clamped);
    reactFlow.setViewport({ x: 0, y: 0, zoom: clamped / 100 }, { duration: 180 });
  };

  const getDelayAtPath = (
    path: FlowPathPart[] | null
  ): { value: number; unit: DelayUnit } => {
    if (!path || !rootNode) return { value: 0, unit: "days" };
    let current: FlowNode | null = rootNode;
    let slot: FlowSlot | null = null;
    for (const step of path) {
      if (!current) break;
      if (current.type === "ifConnection" && (step === "notConnected" || step === "connected")) {
        slot = current[step];
        current = slot.next;
      } else if (current.type === "ifOpenProfile" && (step === "notOpenProfile" || step === "openProfile")) {
        slot = current[step];
        current = slot.next;
      } else if (current.type === "sendConnectionRequest" && (step === "notAccepted" || step === "accepted")) {
        slot = current[step];
        current = slot.next;
      } else if (isReplyBranchNode(current) && (step === "notReplied" || step === "replied")) {
        slot = current[step];
        current = slot.next;
      } else if ((current.type === "likePost" || current.type === "viewProfile") && step === "next") {
        slot = current.next;
        current = slot.next;
      }
    }
    return {
      value: slot?.delayDays ?? 0,
      unit: normalizeDelayUnit(slot?.delayUnit),
    };
  };

  const usedActionTypesAtTarget = React.useMemo(
    () => getActionTypesForPath(rootNode, targetPath),
    [rootNode, targetPath]
  );
  const usedActionTypesGlobal = React.useMemo(() => getAllActionTypes(rootNode), [rootNode]);
  const visibleOptions = React.useMemo(() => {
    return ACTION_OPTIONS.filter((option) => {
      if (!option.actionType) return true;
      if (!usedActionTypesAtTarget.has(option.actionType)) return true;
      return REPEATABLE_ACTIONS.includes(option.actionType);
    });
  }, [usedActionTypesAtTarget]);
  const stepOptions = visibleOptions.filter((option) => option.group === "steps");
  const sequenceTemplates = React.useMemo(
    () => (Array.isArray(templates) ? templates : []).filter(isSequenceTemplate),
    [templates]
  );
  const showTemplatePicker = hydrationDone && !rootNode && !startFromScratch;

  const applySequenceTemplate = (template: any) => {
    const steps = getSequenceStepsFromTemplate(template);
    const nextRoot = buildCampaignRootFromTemplateSteps(steps);
    if (!nextRoot) {
      toast.error("This template has no actions to apply.");
      return;
    }
    if (steps.some((step) => step.actionType === "sendEmail") && !emailChannelVerified) {
      toast.info(EMAIL_CHANNEL_REQUIRED_MESSAGE);
    }
    hasUserEditedGraphRef.current = true;
    hasUserPickedTemplateRef.current = true;
    setAppliedTemplateId(String(template?._id || ""));
    setStartFromScratch(true);
    setRootNode(nextRoot);
  };

  const beginFromScratch = () => {
    hasUserEditedGraphRef.current = true;
    hasUserPickedTemplateRef.current = true;
    setAppliedTemplateId(null);
    setRootNode(null);
    setStartFromScratch(true);
  };

  const openTemplatePicker = () => {
    hasUserEditedGraphRef.current = true;
    setRootNode(null);
    setStartFromScratch(false);
  };

  const handleContinue = () =>
    runContinue(async () => {
    if (!rootNode) {
      toast.error("Please add at least one action to your sequence before continuing.");
      return;
    }
    const actionExecutionTrace: ActionPreviewItem[] = [];
    collectActionPreview(rootNode, "root", 0, "days", actionExecutionTrace);
    const legacyActionsPayload: LegacyActionPreviewItem[] = [];
    collectLegacyActionsPayload(rootNode, "root", 0, "days", legacyActionsPayload);

    // Prevent advancing without any actual sequence actions.
    if (!legacyActionsPayload.length) {
      toast.error("Please add at least one action to your sequence before continuing.");
      return;
    }

    if (usedActionTypesGlobal.has("sendEmail") && !emailChannelVerified) {
      toast.error(EMAIL_CHANNEL_REQUIRED_MESSAGE);
      return;
    }

    const executionPayload = {
      actionExecutionTrace,
      legacyActionsPayload,
    };

    const rehydrationPayload = {
      sequenceGraph: {
        rootNode,
      },
      canvas: {
        nodes,
        edges,
      },
    };

    // React Router location state is stored via `history.pushState` which uses structured cloning.
    // Our ReactFlow nodes/edges can include non-clonable fields, especially when using custom node
    // renderers (functions inside `node.data`). Step 3 hydration only needs `sequenceGraph.rootNode`,
    // so we keep navigation payload minimal to avoid DataCloneErrors.
    const navigationRehydrationPayload = {
      sequenceGraph: {
        rootNode,
      },
    };

    console.log("[Campaign Sequence] execution payload", executionPayload);
    console.log("[Campaign Sequence] legacy actions payload", legacyActionsPayload);
    console.log("[Campaign Sequence] rehydration payload", rehydrationPayload);

    const loader = toast.loading("Saving changes...");
    try {
      const draftResponse = await upsertCampaignDraft({
        campaignId,
        ownerId: resolvedScope.ownerId,
        userId: resolvedScope.userId,
        workspaceId: resolvedScope.workspaceId,
        actions: legacyActionsPayload,
        sequenceRehydration: rehydrationPayload,
        sequenceTemplateId: appliedTemplateId || null,
      });
      const nextCampaignId = draftResponse?.campaignId || campaignId;
      toast.update(loader, {
        render: "Saved successfully.",
        type: "success",
        isLoading: false,
        autoClose: 3000,
        closeOnClick: true,
      });

      navigate(
        {
          pathname: "../schedule",
          search: draftSearchFromCampaignId(nextCampaignId),
        },
        {
          state: {
            ...flowState,
            campaignId: nextCampaignId,
            stepThreePayload: {
              actions: legacyActionsPayload,
            },
            stepThreeRehydration: navigationRehydrationPayload,
          },
        }
      );
    } catch (error) {
      console.error("[Campaign Sequence] Unable to save draft", error);
      const message =
        (error as any)?.response?.data?.error ||
        (error as any)?.response?.data?.details ||
        (error as any)?.message ||
        "Unable to save changes.";
      toast.update(loader, {
        render: message,
        type: "error",
        isLoading: false,
        autoClose: 3000,
        closeOnClick: true,
      });
    }
  });

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div
        className="shrink-0 flex flex-wrap items-center justify-between gap-3 border-b border-light-grey bg-background-grey px-3 py-3 sm:px-6"
        style={{ backgroundImage: "url('/sequence_bg.svg')" }}
      >
        <div className="inline-flex items-center rounded-[8px] border border-light-grey bg-white p-1">
          <button type="button" className="h-7 w-7 rounded text-dark-black hover:bg-primary" onClick={() => applyZoom(zoom - 10)}>-</button>
          <span className="px-2 text-xs font-medium text-dark-black">{zoom}%</span>
          <button type="button" className="h-7 w-7 rounded text-dark-black hover:bg-primary" onClick={() => applyZoom(zoom + 10)}>+</button>
        </div>
        <div className="flex items-center gap-2">
          {showLegacyActionsButton ? (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setIsLegacyActionsModalOpen(true)}
            >
              View Existing Actions
            </Button>
          ) : null}
          {hydrationDone && !showTemplatePicker ? (
            <Button
              variant="outline"
              size="sm"
              className="!border-line text-dark-blue hover:bg-white"
              onClick={openTemplatePicker}
            >
              Use template
            </Button>
          ) : null}
          <Button
            variant="primary"
            size="sm"
            iconSrc="/icons/arrow_right.svg"
            showAfter
            isLoading={isContinuing}
            onClick={handleContinue}
          >
            {isContinuing ? "Saving..." : "Continue"}
          </Button>
        </div>
      </div>

      <div
        className="relative min-h-0 flex-1 overflow-auto bg-background-grey px-3 py-4 sm:px-6"
        style={{ backgroundImage: "url('/sequence_bg.svg')" }}
      >
        <div ref={menuAnchorRef} className="relative mx-auto flex h-[720px] w-full max-w-[1320px] flex-col items-center overflow-x-auto sm:h-[1050px]">
          {!hydrationDone ? (
            <p className="mt-16 text-sm text-muted">Loading sequence...</p>
          ) : showTemplatePicker ? (
            <div className="w-full max-w-[980px] px-2 py-6">
              <h2 className="text-lg font-semibold text-dark-black">Choose a sequence</h2>
              <p className="mt-1 text-sm text-muted">
                Start from a saved template and edit its actions here, or build a new sequence from scratch.
              </p>
              {sequenceTemplates.length > 0 ? (
                <div className="mt-5 grid grid-cols-1 gap-3.5 sm:grid-cols-2">
                  {sequenceTemplates.map((template: any) => {
                    const name = String(template?.name || template?.templateName || "").trim();
                    const path = getSequencePathLabel(getSequenceStepsFromTemplate(template));
                    const id = String(template?._id || name);
                    const isSelected = appliedTemplateId === String(template?._id || "");
                    return (
                      <button
                        key={id}
                        type="button"
                        className={[
                          "flex min-h-[140px] flex-col rounded-[10px] border bg-white p-[17px] text-left hover:border-mainColor hover:shadow-sm",
                          isSelected ? "border-bright-green" : "border-line",
                        ].join(" ")}
                        onClick={() => applySequenceTemplate(template)}
                      >
                        <span className="self-start rounded-xl bg-mini-avatar px-1.5 py-1 text-[10px] font-semibold uppercase tracking-wide text-mini-avatar-fg">
                          Sequence flow
                        </span>
                        <span className="mt-3.5 text-[15px] font-semibold tracking-tight text-dark-blue">
                          {name || "Untitled template"}
                        </span>
                        <span className="mt-1 text-[11px] text-muted">{path}</span>
                        <span className="mt-auto pt-4 text-[10px] font-semibold text-muted">
                          Use template
                        </span>
                      </button>
                    );
                  })}
                </div>
              ) : (
                <p className="mt-5 text-sm text-muted">No sequence templates yet. Build this campaign from scratch.</p>
              )}
              <div className="mt-5">
                <Button
                  variant="outline"
                  size="sm"
                  className="h-10 !border-line px-3.5 font-semibold text-dark-blue hover:bg-white"
                  onClick={beginFromScratch}
                >
                  + Create new sequence
                </Button>
              </div>
            </div>
          ) : (
            <>
          <div className="mb-4 inline-flex items-center gap-2 text-lg font-semibold text-dark-black"><TbAlertTriangle className="h-5 w-5" /><span>Start campaign</span></div>
          <div className="mb-3 flex items-center gap-2">
            <button type="button" className="inline-flex h-10 items-center gap-2 rounded-[8px] border border-bright-green bg-bright-green px-5 text-md font-medium text-dark-black" onClick={(e) => openMenuAt(e, null)}>
              <FiPlus className="h-4 w-4" />
              Add Action
            </button>
           {/* <span className="text-lg text-dark-black">Or</span>
            <button type="button" className="inline-flex h-10 items-center gap-2 rounded-[8px] border border-light-grey bg-white px-5 text-md font-medium text-dark-black" onClick={() => setIsSidebarOpen(true)}>
              <FiSend className="h-4 w-4" />
              Send Invitation
            </button> */}
          </div> 

          {isMenuOpen ? (
            <div ref={menuRef} className="absolute z-30 w-full max-w-[390px] rounded-[8px] border border-light-grey bg-white shadow-sm" style={{ left: menuPos.x, top: menuPos.y }}>
              <div className="border-b border-light-grey px-4 py-2 text-xs font-semibold tracking-wide text-dark-grey">ACTIONS</div>
              {stepOptions.map((option) => {
                const isRepeated = Boolean(option.actionType && usedActionTypesGlobal.has(option.actionType));
                const showRepeatWarning = Boolean(
                  option.actionType && isRepeated && WARN_ON_REPEAT_ACTIONS.includes(option.actionType)
                );
                const emailLocked =
                  option.actionType === "sendEmail" && !emailChannelVerified;
                return (
                  <button
                    key={option.id}
                    type="button"
                    className={[
                      "flex w-full items-center justify-between border-b border-light-grey px-4 py-2 text-left text-md",
                      emailLocked
                        ? "cursor-not-allowed opacity-50"
                        : showRepeatWarning
                          ? "bg-[#F4EFAE] hover:bg-[#eee69a]"
                          : "hover:bg-primary",
                    ].join(" ")}
                    onClick={() => addSelectedAction(option)}
                  >
                    <span>
                      {option.label}
                      {emailLocked ? (
                        <small className="ml-2 text-[11px] text-muted">Verify email first</small>
                      ) : null}
                    </span>
                    {showRepeatWarning ? (
                      <span className="group relative inline-flex">
                        <TbAlertTriangle className="h-4 w-4 text-dark-black" />
                        <span className="pointer-events-none absolute right-0 top-full z-40 mt-2 hidden w-[230px] rounded-[8px] bg-dark-black px-2 py-1 text-xs text-white group-hover:block">
                          {REPEAT_WARNING_TEXT}
                        </span>
                      </span>
                    ) : null}
                  </button>
                );
              })}
            </div>
          ) : null}

          {rootNode ? (
            <div ref={graphRef} className="pointer-events-auto mt-5 h-full w-full rounded-[8px]">
              <ReactFlow
                nodes={nodes}
                edges={edges}
                onNodesChange={onNodesChange}
                onEdgesChange={onEdgesChange}
                defaultViewport={{ x: 0, y: 0, zoom: 1 }}
                minZoom={0.2}
                maxZoom={2}
                connectionMode={ConnectionMode.Loose}
                nodesDraggable={false}
                zoomOnDoubleClick={false}
                zoomOnScroll={false}
                zoomOnPinch={false}
                preventScrolling={false}
                proOptions={{ hideAttribution: true }}
                nodeTypes={{ actionCard: ActionCardNode }}
                onNodeClick={(event: React.MouseEvent, node: Node<GraphNodeData>) => {
                  if (node.id.startsWith("add:")) {
                    const path = node.id.replace("add:", "").split(".") as FlowPathPart[];
                    openMenuAt(event, path);
                    return;
                  }
                  if (node.id.startsWith("end:")) {
                    const path = node.id.replace("end:", "").split(".") as FlowPathPart[];
                    setRootNode((prev) => updateSlot(prev, path, (slot) => ({ ...slot, ended: true })));
                    return;
                  }
                  if (node.id.startsWith("ended:")) {
                    const path = node.id.replace("ended:", "").split(".") as FlowPathPart[];
                    setRootNode((prev) => updateSlot(prev, path, (slot) => ({ ...slot, ended: false })));
                    return;
                  }
                  if (node.id.startsWith("delay:")) {
                    const path = node.id.replace("delay:", "").split(".") as FlowPathPart[];
                    setEditingDelayPath(path);
                  }
                }}
              >
                <Background color="#e5e5e5" gap={22} size={1} />
              </ReactFlow>
            </div>
          ) : null}
            </>
          )}
        </div>
      </div>

      <SequenceConnectionRequestSidebar
        isOpen={isSidebarOpen}
        initialConfig={editingConnectionRequestConfig}
        onClose={() => {
          setPendingType(null);
          setIsSidebarOpen(false);
          setIsEditingRootAction(false);
          setEditingConnectionRequestConfig(null);
        }}
        onSave={(payload) => {
          if (pendingType !== "sendConnectionRequest") {
            setPendingType(null);
            setIsSidebarOpen(false);
            setIsEditingRootAction(false);
            setEditingConnectionRequestConfig(null);
            return;
          }

          const isEditingExisting = editingConnectionRequestConfig !== null;

          if (isEditingExisting) {
            setRootNode((prev) => {
              if (!prev) return prev;
              if (!targetPath || targetPath.length === 0) {
                if (prev.type === "sendConnectionRequest") {
                  return mergeConnectionRequestNodeConfig(prev, payload);
                }
                return prev;
              }
              return updateSlot(prev, targetPath, (slot) => {
                if (slot.next?.type === "sendConnectionRequest") {
                  return {
                    ...slot,
                    next: mergeConnectionRequestNodeConfig(slot.next, payload),
                  };
                }
                return slot;
              });
            });
          } else {
            const nodeWithConfig = makeConnectionRequestNodeWithConfig(payload);
            if (targetPath && targetPath.length > 0) {
              setRootNode((prev) =>
                updateSlot(prev, targetPath, (slot) => ({
                  ...slot,
                  ended: false,
                  next: nodeWithConfig,
                }))
              );
            } else {
              setRootNode(nodeWithConfig);
            }
          }

          setPendingType(null);
          setIsSidebarOpen(false);
          setIsEditingRootAction(false);
          setEditingConnectionRequestConfig(null);
        }}
      />

      <SendInMailSidebar
        isOpen={isSendInMailSidebarOpen}
        variant={pendingType === "sendEmail" ? "email" : "linkedin"}
        initialConfig={editingSendInMailConfig}
        onClose={() => {
          setPendingType(null);
          setIsSendInMailSidebarOpen(false);
          setIsEditingRootAction(false);
          setEditingSendInMailConfig(null);
        }}
        onSave={(payload) => {
          if (!isReplyBranchType(pendingType)) {
            setPendingType(null);
            setIsSendInMailSidebarOpen(false);
            setIsEditingRootAction(false);
            setEditingSendInMailConfig(null);
            return;
          }
          const actionType = pendingType;

          const isEditingExisting = editingSendInMailConfig !== null;

          if (isEditingExisting) {
            setRootNode((prev) => {
              if (!prev) return prev;
              if (!targetPath || targetPath.length === 0) {
                if (isReplyBranchNode(prev) && prev.type === actionType) {
                  return mergeSendInMailNodeConfig(prev, payload);
                }
                return prev;
              }
              return updateSlot(prev, targetPath, (slot) => {
                if (slot.next && isReplyBranchNode(slot.next) && slot.next.type === actionType) {
                  return {
                    ...slot,
                    next: mergeSendInMailNodeConfig(slot.next, payload),
                  };
                }
                return slot;
              });
            });
          } else {
            const nodeWithConfig = makeSendInMailNodeWithConfig(payload, actionType);
            if (targetPath && targetPath.length > 0) {
              setRootNode((prev) =>
                updateSlot(prev, targetPath, (slot) => ({
                  ...slot,
                  ended: false,
                  next: nodeWithConfig,
                }))
              );
            } else {
              setRootNode(nodeWithConfig);
            }
          }

          setPendingType(null);
          setIsSendInMailSidebarOpen(false);
          setIsEditingRootAction(false);
          setEditingSendInMailConfig(null);
        }}
      />

      <DelayBeforeActionModal
        isOpen={editingDelayPath !== null}
        onClose={() => setEditingDelayPath(null)}
        initialValue={getDelayAtPath(editingDelayPath).value}
        initialUnit={getDelayAtPath(editingDelayPath).unit}
        onSave={(nextValue, nextUnit) => {
          if (!editingDelayPath) return;
          setRootNode((prev) =>
            updateSlot(prev, editingDelayPath, (slot) => ({
              ...slot,
              delayDays: nextValue,
              delayUnit: nextUnit,
            }))
          );
          setEditingDelayPath(null);
        }}
      />

      <Modal
        isOpen={isLegacyActionsModalOpen}
        onClose={() => setIsLegacyActionsModalOpen(false)}
        title="Existing Campaign Actions"
        size="medium"
      >
        <div className="space-y-3">
          {legacyReadonlyActions.map((action, index) => (
            <div
              key={`${action._debugPath}-${index}`}
              className="rounded-[10px] border border-light-grey bg-white px-4 py-3"
            >
              <div className="flex items-center justify-between gap-3">
                <p className="text-sm font-semibold text-dark-black">
                  {index + 1}. {legacyActionTitle(action.type)}
                </p>
                {action.delay && action.delay > 0 ? (
                  <span className="rounded-full bg-bg-blue px-2 py-0.5 text-xs text-brand-blue">
                    Delay: {action.delay} {action.delay === 1 ? action.delayUnit?.slice(0, -1) || "day" : action.delayUnit || "days"}
                  </span>
                ) : null}
              </div>
              {action.type === "invitation" && action.customMessage ? (
                <p className="mt-2 text-sm text-dark-grey">{action.customMessage}</p>
              ) : null}
              {(action.type === "message" || action.type === "messageWithDelay") ? (
                <div className="mt-2 space-y-1 text-sm text-dark-grey">
                  {action.message?.subject ? (
                    <p>
                      <span className="font-medium text-dark-black">Subject:</span> {action.message.subject}
                    </p>
                  ) : null}
                  {action.message?.body ? (
                    <p>
                      <span className="font-medium text-dark-black">Message:</span> {action.message.body}
                    </p>
                  ) : null}
                </div>
              ) : null}
            </div>
          ))}
          {legacyReadonlyActions.length === 0 ? (
            <p className="text-sm text-dark-grey">No existing actions found.</p>
          ) : null}
          <div className="flex justify-end pt-2">
            <Button variant="primary" size="sm" onClick={() => setIsLegacyActionsModalOpen(false)}>
              Close
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}

export default function CampaignCreateSequenceStep() {
  return (
    <ReactFlowProvider>
      <SequenceCanvas />
    </ReactFlowProvider>
  );
}
